import { isValid } from "date-fns";
import type { ConditionFilterDescriptor } from "../../../../../../../../../api/types/fieldConfigurationTypes.ts";
import { ConditionEqualityType } from "../../../../../../../../../api/types/fieldConfigurationTypes.ts";
import { formatToISODate, getValidatedDate } from "../../../../../../utils/dateUtils.ts";
import type { RelativeDateValue } from "../../../../../../utils/relativeDates.ts";
import {
  isRelativeDayValue,
  isRelativePeriodValue,
  resolveRelativeDay,
  resolveRelativePeriod,
} from "../../../../../../utils/relativeDates.ts";

export type DateMode = "exact" | "reference";

export enum DateType {
  Period = 0,
  Date = 1,
}

export const singleDateDefaultEqualityType = ConditionEqualityType.Equal;

export type ExactDateRange = [Date | null, Date | null];

export interface DateFilterState {
  mode: DateMode;
  periodType: DateType;
  dayEqualityType: ConditionEqualityType;
  exactDay: Date | null;
  exactRange: ExactDateRange;
  reference: RelativeDateValue | undefined;
}

const toValidDate = (value: string | null | undefined): Date | null => {
  const date = getValidatedDate(value);
  return date && isValid(date) ? date : null;
};

export const parseFilterState = (filter: ConditionFilterDescriptor | undefined): DateFilterState => {
  const base: DateFilterState = {
    mode: "exact",
    periodType: DateType.Date,
    dayEqualityType: singleDateDefaultEqualityType,
    exactDay: null,
    exactRange: [null, null],
    reference: undefined,
  };
  const values = filter?.values ?? [];
  const first = values[0] ?? undefined;
  const equalityType = filter?.equalityType ?? singleDateDefaultEqualityType;

  if (isRelativePeriodValue(first)) {
    return { ...base, mode: "reference", reference: first };
  }
  if (isRelativeDayValue(first)) {
    return { ...base, mode: "reference", reference: first, dayEqualityType: equalityType };
  }
  if (values.length >= 2) {
    return { ...base, periodType: DateType.Period, exactRange: [toValidDate(values[0]), toValidDate(values[1])] };
  }
  if (values.length === 1) {
    return { ...base, periodType: DateType.Date, exactDay: toValidDate(values[0]), dayEqualityType: equalityType };
  }
  return base;
};

export const changeModeState = (state: DateFilterState, nextMode: DateMode): Partial<DateFilterState> => {
  if (nextMode !== "exact" || !state.reference) {
    return { mode: nextMode };
  }
  if (isRelativeDayValue(state.reference)) {
    return { mode: nextMode, periodType: DateType.Date, exactDay: resolveRelativeDay(state.reference) };
  }
  return { mode: nextMode, periodType: DateType.Period, exactRange: resolveRelativePeriod(state.reference) };
};

export const changePeriodTypeState = (state: DateFilterState, nextPeriodType: DateType): Partial<DateFilterState> => {
  if (nextPeriodType === DateType.Date) {
    return {
      periodType: nextPeriodType,
      dayEqualityType: singleDateDefaultEqualityType,
      exactDay: state.exactRange[1],
    };
  }
  return { periodType: nextPeriodType, exactRange: [null, state.exactDay] };
};

export const buildFilter = (
  state: DateFilterState,
  dimensionName: string
): { filter: ConditionFilterDescriptor; isValid: boolean } => {
  const values: (string | null)[] = [];
  let equalityType: ConditionEqualityType;
  let valid: boolean;

  if (state.mode === "reference") {
    if (state.reference) {
      values.push(state.reference);
    }
    equalityType = isRelativePeriodValue(state.reference) ? ConditionEqualityType.Equal : state.dayEqualityType;
    valid = values.length === 1;
  } else if (state.periodType === DateType.Period) {
    equalityType = ConditionEqualityType.Equal;
    const [from, to] = state.exactRange;
    const fromStr = from ? formatToISODate(from) : "";
    const toStr = to ? formatToISODate(to) : "";
    if (fromStr) {
      values.push(fromStr);
    }
    if (toStr) {
      values.push(toStr);
    }
    valid = values.length === 2 && !(from !== null && to !== null && from > to);
  } else {
    equalityType = state.dayEqualityType;
    const dayStr = state.exactDay ? formatToISODate(state.exactDay) : "";
    if (dayStr) {
      values.push(dayStr);
    }
    valid = values.length === 1;
  }

  return { filter: { dimensionName, values, equalityType }, isValid: valid };
};
